import { coreApi } from '$api'
import { ShiftRequestStatus } from '$constants'
import { singletonQueryClient } from '$utils'
import {
  ResolveShiftRequestModel,
  ShiftRequest,
  UpdateShiftRequestStatusModel,
} from '$models'

enum QueryKeys {
  ShiftRequest = 'shift-request',
}

/**
 * Get a shift request by id, with its lifecycle actions, for the manager's detail panel.
 */
export const getById = async (id: string): Promise<ShiftRequest> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.ShiftRequest, id],
    queryFn: async () => coreApi.getShiftRequestById(id),
  })
}

/**
 * Approve a shift request on the manager's behalf, with an optional note. The shift moves to the
 * recipient. Returns the updated (Approved) request.
 */
export const approve = async (
  id: string,
  note?: string
): Promise<ShiftRequest> => {
  return await coreApi.approveShiftRequestAsManager(
    id,
    new ResolveShiftRequestModel({ note })
  )
}

/**
 * Decline a shift request on the manager's behalf, with an optional note. The shift stays with
 * the initiator. Returns the updated (Declined) request.
 */
export const decline = async (
  id: string,
  note?: string
): Promise<ShiftRequest> => {
  return await coreApi.declineShiftRequestAsManager(
    id,
    new ResolveShiftRequestModel({ note })
  )
}

/**
 * Approve or decline a shift request straight from the manager-approval email's one-click links.
 * The request is authenticated by the one-time token carried in the page URL, so this runs even
 * when the manager is not otherwise signed in.
 */
export const updateStatus = async (
  id: string,
  status: ShiftRequestStatus
): Promise<ShiftRequest> => {
  return await coreApi.updateShiftRequestStatus(
    id,
    new UpdateShiftRequestStatusModel({ status })
  )
}
